import store from '@/store'
import { getFilePath } from '@/utils/qiniu'

const AI_CHATBOT_EMBED_SCRIPT_ID = 'ai-chatbot-embed-script'
const AI_CHATBOT_GLOBAL = 'AiChatbot'

function getAiChatbotEmbedSrc () {
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:8080/ai-chatbot/ai-chatbot.js'
  }
  const timestamp = new Date().getTime()
  return `${process.env.VUE_APP_QINIU_DOMAIN}${process.env.VUE_APP_AI_CHATBOT_CDN_KEY_PREFIX}/embed/ai-chatbot.js?t=${timestamp}`
}

let scriptLoadingPromise = null

function mapLocaleToEmbedLang (locale) {
  return locale === 'en' ? 'en' : 'zh'
}

function buildAiChatbotEmbedOptions (options = {}) {
  const companyThemeSetting = store.getters['company/companyThemeSetting']
  const customColor = companyThemeSetting?.commonSetting?.customColor || '#0096cc'
  const {
    company,
    companyId,
    publicConfig,
    jobId,
    userId,
    visitorId,
    locale,
    position
  } = options

  const lang = mapLocaleToEmbedLang(locale)
  const title = publicConfig?.name || (company && (company.chatbot_name || company.i18nName || company.name || company.cn_name))
  const titleCn = publicConfig?.name_cn || publicConfig?.name
  const scopedUserId = userId
  let iconUrl = publicConfig?.avatar
  if (iconUrl) {
    iconUrl = getFilePath(iconUrl)
  }

  return {
    position: position || 'bottom-right',
    color: publicConfig?.theme_color || customColor,
    lang,
    open: false,
    title: title,
    titleCn,
    iconUrl,
    visitorId,
    welcomeMessage: publicConfig?.welcome_message,
    welcomeMessageCn: publicConfig?.welcome_message_cn,
    systemPromptOverride: publicConfig?.system_prompt_override,
    companyId: companyId,
    jobId: jobId,
    userId: scopedUserId
  }
}

function getAiChatbotEmbedApi () {
  if (typeof window === 'undefined') {
    return null
  }

  return window[AI_CHATBOT_GLOBAL] || null
}

function loadAiChatbotEmbedScript () {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window is not available'))
  }
  const existingApi = getAiChatbotEmbedApi()
  if (existingApi) {
    return Promise.resolve(existingApi)
  }

  if (scriptLoadingPromise) {
    return scriptLoadingPromise
  }

  const embedSrc = getAiChatbotEmbedSrc()

  scriptLoadingPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(AI_CHATBOT_EMBED_SCRIPT_ID)
    if (existingScript) {
      const loadedApi = getAiChatbotEmbedApi()
      if (loadedApi) {
        resolve(loadedApi)
        return
      }
      existingScript.addEventListener('load', () => resolve(getAiChatbotEmbedApi()), { once: true })
      existingScript.addEventListener('error', () => {
        existingScript.remove()
        reject(new Error(`Failed to load ${existingScript.src || embedSrc}`))
      }, { once: true })
      return
    }

    const script = document.createElement('script')
    script.id = AI_CHATBOT_EMBED_SCRIPT_ID
    script.async = true
    script.src = embedSrc
    script.onload = () => {
      const loadedApi = getAiChatbotEmbedApi()
      if (loadedApi) {
        resolve(loadedApi)
        return
      }

      reject(new Error(`AiChatbot SDK loaded but window.${AI_CHATBOT_GLOBAL} was not found`))
    }
    script.onerror = () => {
      script.remove()
      reject(new Error(`Failed to load ${embedSrc}`))
    }
    document.head.appendChild(script)
  }).catch((error) => {
    scriptLoadingPromise = null
    throw error
  })

  return scriptLoadingPromise
}

export {
  AI_CHATBOT_GLOBAL,
  getAiChatbotEmbedApi,
  getAiChatbotEmbedSrc,
  buildAiChatbotEmbedOptions,
  loadAiChatbotEmbedScript
}
